interface ProfileData {
  name: string;
  age?: number;
  nationality?: string;
  occupation: string[];
  bio: string;
  avatar?: string;
  birthYear?: number;
  location?: string;
}

interface TimelineEvent {
  year: number;
  title: string;
  description: string;
  category: 'birth' | 'education' | 'career' | 'achievement' | 'personal' | 'milestone';
  location?: string;
  details?: string;
}

interface Source {
  name: string;
  url: string;
  reliability: 'high' | 'medium' | 'low';
  lastUpdated: string;
  icon: string;
  verified: boolean;
  dataPoints: number;
}

export const mockProfiles: Record<string, ProfileData> = {
  'Elon Musk': {
    name: 'Elon Musk',
    age: 52,
    nationality: 'South African-American',
    occupation: ['Entrepreneur', 'Engineer', 'Inventor', 'Business Magnate'],
    bio: 'Visionary entrepreneur and engineer known for revolutionizing multiple industries including electric vehicles, space exploration, and neural interfaces. CEO of Tesla and SpaceX, with a mission to accelerate sustainable transport and make life multiplanetary.',
    location: 'Austin, Texas',
    birthYear: 1971
  },
  'Steve Jobs': {
    name: 'Steve Jobs',
    age: 56,
    nationality: 'American',
    occupation: ['Entrepreneur', 'Inventor', 'Designer', 'Business Executive'],
    bio: 'Iconic co-founder and former CEO of Apple Inc. who revolutionized personal computing, animated movies, music, phones, tablet computing, and digital publishing. Known for his perfectionism and innovative product design philosophy.',
    location: 'Palo Alto, California',
    birthYear: 1955
  },
  'Dharmateja Kadem': {
    name: 'Dharmateja Kadem',
    age: 28,
    nationality: 'Indian-American',
    occupation: ['Software Engineer', 'Entrepreneur', 'AI Researcher'],
    bio: 'Emerging tech entrepreneur and AI researcher specializing in machine learning and neural networks. Known for innovative approaches to human-computer interaction and building intelligent systems that enhance human capabilities.',
    location: 'San Francisco, California',
    birthYear: 1996
  }
};

export const mockTimelines: Record<string, TimelineEvent[]> = {
  'Elon Musk': [
    {
      year: 1971,
      title: 'Born in Pretoria',
      description: 'Born in Pretoria, South Africa, to Maye and Errol Musk.',
      category: 'birth',
      location: 'Pretoria, South Africa'
    },
    {
      year: 1995,
      title: 'Graduated from UPenn',
      description: 'Received bachelor degrees in economics and physics from University of Pennsylvania.',
      category: 'education',
      location: 'Philadelphia, PA'
    },
    {
      year: 1996,
      title: 'Co-founded Zip2',
      description: 'Co-founded web software company Zip2 with his brother Kimbal.',
      category: 'career',
      location: 'Palo Alto, CA'
    },
    {
      year: 1999,
      title: 'Sold Zip2 to Compaq',
      description: 'Sold Zip2 to Compaq for $307 million, earning $22 million from the sale.',
      category: 'achievement',
      location: 'California, USA'
    },
    {
      year: 2002,
      title: 'Founded SpaceX',
      description: 'Founded Space Exploration Technologies Corp (SpaceX) to reduce space transportation costs.',
      category: 'career',
      location: 'Hawthorne, CA'
    },
    {
      year: 2004,
      title: 'Joined Tesla',
      description: 'Joined Tesla Motors as chairman and product architect, later becoming CEO.',
      category: 'career',
      location: 'San Carlos, CA'
    },
    {
      year: 2020,
      title: 'Tesla Stock Surge',
      description: 'Tesla stock surged, making Musk one of the richest people in the world.',
      category: 'achievement',
      location: 'Global'
    }
  ],
  'Steve Jobs': [
    {
      year: 1955,
      title: 'Born in San Francisco',
      description: 'Born to Abdulfattah Jandali and Joanne Schieble, later adopted by Paul and Clara Jobs.',
      category: 'birth',
      location: 'San Francisco, CA'
    },
    {
      year: 1976,
      title: 'Co-founded Apple',
      description: 'Co-founded Apple Computer Company with Steve Wozniak in the Jobs family garage.',
      category: 'career',
      location: 'Los Altos, CA'
    },
    {
      year: 1984,
      title: 'Launched Macintosh',
      description: 'Introduced the first Macintosh personal computer with revolutionary graphical interface.',
      category: 'achievement',
      location: 'Cupertino, CA'
    },
    {
      year: 1985,
      title: 'Left Apple',
      description: 'Resigned from Apple following boardroom coup and founded NeXT Computer.',
      category: 'career',
      location: 'California, USA'
    },
    {
      year: 1997,
      title: 'Returned to Apple',
      description: 'Returned to Apple as interim CEO after Apple acquired NeXT.',
      category: 'career',
      location: 'Cupertino, CA'
    },
    {
      year: 2007,
      title: 'Launched iPhone',
      description: 'Introduced the first iPhone, revolutionizing the smartphone industry.',
      category: 'achievement',
      location: 'San Francisco, CA'
    }
  ],
  'Dharmateja Kadem': [
    {
      year: 1996,
      title: 'Born in Hyderabad',
      description: 'Born in Hyderabad, India, showing early interest in technology and programming.',
      category: 'birth',
      location: 'Hyderabad, India'
    },
    {
      year: 2018,
      title: 'Graduated from IIT',
      description: 'Completed B.Tech in Computer Science from Indian Institute of Technology.',
      category: 'education',
      location: 'India'
    },
    {
      year: 2019,
      title: 'Joined Google',
      description: 'Started as Software Engineer at Google, working on AI and machine learning projects.',
      category: 'career',
      location: 'Mountain View, CA'
    },
    {
      year: 2021,
      title: 'Published AI Research',
      description: 'Published breakthrough research on neural network optimization in top-tier conferences.',
      category: 'achievement',
      location: 'San Francisco, CA'
    },
    {
      year: 2023,
      title: 'Founded AI Startup',
      description: 'Founded innovative AI startup focusing on human-computer interaction.',
      category: 'career',
      location: 'San Francisco, CA'
    },
    {
      year: 2024,
      title: 'Series A Funding',
      description: 'Successfully raised $10M Series A funding for AI startup.',
      category: 'achievement',
      location: 'Silicon Valley, CA'
    }
  ]
};

export const mockSources: Record<string, Source[]> = {
  'Elon Musk': [
    {
      name: 'Wikipedia',
      url: 'https://en.wikipedia.org/wiki/Elon_Musk',
      reliability: 'high',
      lastUpdated: '2 hours ago',
      icon: '📖',
      verified: true,
      dataPoints: 127
    },
    {
      name: 'Tesla Official',
      url: 'https://www.tesla.com',
      reliability: 'high',
      lastUpdated: '1 day ago',
      icon: '🚗',
      verified: true,
      dataPoints: 89
    },
    {
      name: 'SpaceX',
      url: 'https://www.spacex.com',
      reliability: 'high',
      lastUpdated: '3 hours ago',
      icon: '🚀',
      verified: true,
      dataPoints: 156
    },
    {
      name: 'Forbes',
      url: 'https://www.forbes.com',
      reliability: 'high',
      lastUpdated: '1 day ago',
      icon: '💰',
      verified: true,
      dataPoints: 67
    }
  ],
  'Steve Jobs': [
    {
      name: 'Apple Official',
      url: 'https://www.apple.com',
      reliability: 'high',
      lastUpdated: '1 day ago',
      icon: '🍎',
      verified: true,
      dataPoints: 203
    },
    {
      name: 'Biography.com',
      url: 'https://www.biography.com',
      reliability: 'high',
      lastUpdated: '3 days ago',
      icon: '📚',
      verified: true,
      dataPoints: 145
    },
    {
      name: 'Britannica',
      url: 'https://www.britannica.com',
      reliability: 'high',
      lastUpdated: '1 week ago',
      icon: '🏛️',
      verified: true,
      dataPoints: 98
    }
  ],
  'Dharmateja Kadem': [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com',
      reliability: 'medium',
      lastUpdated: '1 day ago',
      icon: '💼',
      verified: true,
      dataPoints: 23
    },
    {
      name: 'GitHub',
      url: 'https://github.com',
      reliability: 'medium',
      lastUpdated: '6 hours ago',
      icon: '🐙',
      verified: true,
      dataPoints: 45
    },
    {
      name: 'Research Papers',
      url: 'https://scholar.google.com',
      reliability: 'high',
      lastUpdated: '2 weeks ago',
      icon: '🎓',
      verified: true,
      dataPoints: 12
    }
  ]
};

// Default/placeholder data for unknown people
export const generatePlaceholderData = (name: string) => ({
  profile: {
    name,
    occupation: ['Person of Interest', 'Individual'],
    bio: `${name} is an individual whose story is yet to be fully documented in our database. We're constantly working to gather more information about remarkable people from all walks of life.`,
    location: 'Unknown'
  },
  timeline: [
    {
      year: new Date().getFullYear() - 30,
      title: 'Early Life',
      description: `${name} was born and began their life journey.`,
      category: 'birth' as const,
      location: 'Unknown'
    },
    {
      year: new Date().getFullYear() - 10,
      title: 'Career Development',
      description: `${name} began building their professional career.`,
      category: 'career' as const,
      location: 'Unknown'
    },
    {
      year: new Date().getFullYear() - 5,
      title: 'Notable Achievement',
      description: `${name} achieved recognition in their field.`,
      category: 'achievement' as const,
      location: 'Unknown'
    }
  ],
  sources: []
});